import type { PayloadAction } from '@reduxjs/toolkit';
import type { MetaDataObj, TagData } from 'src/helper/shared-types';
import {
  type InitialQuoteStateType,
  type QuoteAPIResponseObj,
  type QuoteDataObj,
  quoteInitialState,
} from 'src/redux/quotes/types';
import {
  addToListOrReorderTheList,
  type NewItemCreationEvent,
  removeDataIdFromList,
  setGroupedDataMetaLoadingReducer,
  updateDataByGroupOptions,
} from '../common/reducers';

const reducers = {
  setGroupedDataMetaLoading: setGroupedDataMetaLoadingReducer,

  clearQuoteData: (state: InitialQuoteStateType) => {
    state.data = quoteInitialState?.data;
    state.groupedData = quoteInitialState?.groupedData;
    state.groupedDataMeta = quoteInitialState?.groupedDataMeta;
  },
  updateQuoteAssigneeDetails: (
    state: InitialQuoteStateType,
    action: PayloadAction<{
      quoteId: string;
      data: { name: string; profile_pic: string; user_id: string };
      key: string;
      event: string;
    }>,
  ) => {
    if (action?.payload?.data?.user_id && action?.payload?.quoteId) {
      const currentData = { ...(state.source || {}) };

      let updatedAssignee = {} as QuoteDataObj;
      if (action?.payload?.event === 'workorder.assignee.add') {
        const doesAssigneeExist = (state.source?.[action?.payload?.quoteId]?.assignees || [])?.some(
          (item) => item?.user_id === action?.payload?.data?.user_id,
        );
        if (!doesAssigneeExist) {
          updatedAssignee = {
            ...(state.source[action?.payload?.quoteId] || {}),
            [action?.payload?.key]: [
              ...(state.source?.[action?.payload?.quoteId]?.assignees || []),
              action?.payload?.data,
            ],
          };
          state.source = { ...currentData, [action?.payload?.quoteId]: updatedAssignee };
        }
      }
      if (action?.payload?.event === 'workorder.assignee.delete') {
        updatedAssignee = {
          ...(state.source?.[action?.payload?.quoteId] || {}),
          [action?.payload?.key]: [
            ...(state.source?.[action?.payload?.quoteId]?.assignees || []),
          ]?.filter((item) => item?.user_id !== action?.payload?.data?.user_id),
        };
        state.source = { ...currentData, [action?.payload?.quoteId]: updatedAssignee };
      }
    }
  },

  updateQuoteCategoryDetails: (
    state: InitialQuoteStateType,
    action: PayloadAction<{
      quoteId: string;
      data: {
        label: string;
      };
    }>,
  ) => {
    const currentData = { ...(state.source || {}) };
    const quoteItemToUpdate = { ...(currentData[action?.payload?.quoteId] || {}) } as QuoteDataObj;
    const newWoTasks = [...(quoteItemToUpdate?.category || [])];
    // Update the first category label if it exists
    if (newWoTasks.length > 0) {
      newWoTasks[0] = {
        ...(newWoTasks[0] || {}),
        label: action.payload.data.label,
      };
    } else {
      // If there are no existing tasks, initialize with the new label
      newWoTasks.push({ label: action.payload.data.label });
    }
    const updatedWorkOrder = {
      ...quoteItemToUpdate,
      category: newWoTasks as [{ label: string }],
    };
    state.source = {
      ...currentData,
      [action?.payload?.quoteId]: updatedWorkOrder,
    };
  },

  updateQuoteDetailsPropertyAddress: (
    state: InitialQuoteStateType,
    action: PayloadAction<{
      quoteId: string;
      data: {
        address: {
          street_address: string;
          zip_code: string;
          city: string;
          apt_suite_unit: string;
          state_code: string;
        };
      };
    }>,
  ) => {
    const currentData = { ...(state.source || {}) };
    const updatedAddress = {
      ...(state.source[action?.payload?.quoteId] || {}),
      property: {
        ...(state.source[action?.payload?.quoteId]?.property || {}),
        address: action?.payload?.data?.address,
      },
    };

    state.source = { ...currentData, [action?.payload?.quoteId]: updatedAddress };
  },

  updateQuotesDataByGroupOptions: (
    state: InitialQuoteStateType,
    action: PayloadAction<{
      response: { data: Array<QuoteDataObj>; meta: MetaDataObj };
      groupByOptionsList: Array<string>;
    }>,
  ) => {
    updateDataByGroupOptions(state, action, 'quote_id');
  },

  updateQuotesTags: (
    state: InitialQuoteStateType,
    action: PayloadAction<{
      quoteId: string;
      data: TagData;
      key: string;
      actionType: string;
    }>,
  ) => {
    const { actionType, data, key, quoteId } = action.payload;

    const currentData = { ...(state.source || {}) };
    const newSource = { ...(state.tagSource || {}) };
    let updatedQuoteData;
    if (actionType === 'remove') {
      updatedQuoteData = {
        ...(state.source[quoteId] || {}),
        [key]: [...(state?.source?.[quoteId]?.tags || []).filter((id) => id !== data?.tag_id)],
      };
      delete newSource?.[data?.tag_id];
    } else {
      updatedQuoteData = {
        ...(state.source[quoteId] || {}),
        [key]: [...(state?.source?.[quoteId]?.tags || []), data?.tag_id],
      };
      newSource[data?.tag_id] = {
        ...(newSource?.[data?.tag_id] || {}),
        ...data,
      };
    }
    state.source = { ...currentData, [quoteId]: updatedQuoteData };
    state.tagSource = newSource;
  },

  addToQuoteListOrReorderTheList: (
    state: InitialQuoteStateType,
    action: PayloadAction<{
      id: string;
      isGroupBySelected: boolean;
      data: QuoteAPIResponseObj;
      listKey: 'quotes';
      pusherEvent: NewItemCreationEvent;
    }>,
  ) => {
    addToListOrReorderTheList(state, action);
  },
  shouldSidePanel: (
    state: InitialQuoteStateType,
    action: PayloadAction<InitialQuoteStateType['showSidePanel']>,
  ) => {
    state.showSidePanel = action.payload;
  },
  removeQuoteIdFromList: (state: InitialQuoteStateType, action: PayloadAction<string>) => {
    removeDataIdFromList(state, action);
  },
};

export default reducers;
